import { Link } from "react-router-dom";
import { Flower, ArrowLeft, Printer, CheckCircle, WarningCircle, MinusCircle } from "@phosphor-icons/react";
import SiteFooter from "@/components/SiteFooter";
import usePageMeta from "@/lib/usePageMeta";

// VPAT 2.5 INT (International) — Wingwomen, Wingwomen, Inc.
// Level A + Level AA criteria per WCAG 2.1 / EN 301 549 / Section 508.
//
// Support levels:
//  supports      — Fully supported
//  partial       — Partially supported (see remarks)
//  does-not      — Does not support (see remarks)
//  not-applicable — Not applicable to this product

const LEVEL_A = [
  { c: "1.1.1", n: "Non-text Content", s: "supports", r: "All meaningful images have alt text; decorative images use empty alt or aria-hidden. Icon-only buttons have aria-label." },
  { c: "1.2.1", n: "Audio-only and Video-only (Prerecorded)", s: "not-applicable", r: "No audio-only or video-only prerecorded media currently on the platform." },
  { c: "1.2.2", n: "Captions (Prerecorded)", s: "not-applicable", r: "No prerecorded video with audio currently on the platform." },
  { c: "1.2.3", n: "Audio Description or Media Alternative (Prerecorded)", s: "not-applicable", r: "No prerecorded synchronized media currently on the platform." },
  { c: "1.3.1", n: "Info and Relationships", s: "supports", r: "Semantic HTML with proper heading hierarchy, landmarks (main, nav, header, footer), lists, and form labels." },
  { c: "1.3.2", n: "Meaningful Sequence", s: "supports", r: "DOM order matches visual reading order; no CSS-based re-ordering that breaks screen readers." },
  { c: "1.3.3", n: "Sensory Characteristics", s: "supports", r: "Instructions do not rely solely on color, shape, size, or spatial location." },
  { c: "1.4.1", n: "Use of Color", s: "supports", r: "Interactive states, errors, and warnings use icons and text in addition to color." },
  { c: "1.4.2", n: "Audio Control", s: "not-applicable", r: "No auto-playing audio. AI Navigator TTS requires explicit user activation." },
  { c: "2.1.1", n: "Keyboard", s: "supports", r: "All functionality is operable via keyboard. Focus indicator is visible (coral outline on focus-visible)." },
  { c: "2.1.2", n: "No Keyboard Trap", s: "supports", r: "Focus can be moved away from any component using standard keyboard navigation." },
  { c: "2.1.4", n: "Character Key Shortcuts", s: "not-applicable", r: "No single-character keyboard shortcuts implemented." },
  { c: "2.2.1", n: "Timing Adjustable", s: "supports", r: "No time limits on user tasks. Disappearing messages have 24h/7d/permanent options selected by the user." },
  { c: "2.2.2", n: "Pause, Stop, Hide", s: "supports", r: "Non-essential animations respect prefers-reduced-motion. AI response streaming can be scrolled/paused visually." },
  { c: "2.3.1", n: "Three Flashes or Below Threshold", s: "supports", r: "No flashing content." },
  { c: "2.4.1", n: "Bypass Blocks", s: "supports", r: "Skip-to-main-content link is the first focusable element on every page." },
  { c: "2.4.2", n: "Page Titled", s: "supports", r: "Every page has a unique, descriptive title via usePageMeta hook." },
  { c: "2.4.3", n: "Focus Order", s: "supports", r: "Focus order matches visual and logical reading order across all pages." },
  { c: "2.4.4", n: "Link Purpose (In Context)", s: "supports", r: "Link text is descriptive; when text alone is ambiguous, aria-label provides context." },
  { c: "2.5.1", n: "Pointer Gestures", s: "supports", r: "All functionality uses single-point activations; no multi-point or path-based gestures required." },
  { c: "2.5.2", n: "Pointer Cancellation", s: "supports", r: "Down-event does not trigger action; standard click semantics preserved." },
  { c: "2.5.3", n: "Label in Name", s: "supports", r: "Accessible name for interactive elements contains or matches visible label text." },
  { c: "2.5.4", n: "Motion Actuation", s: "not-applicable", r: "No motion-actuated functionality." },
  { c: "3.1.1", n: "Language of Page", s: "supports", r: "HTML lang='en' declared. Additional languages planned for Q2 2026." },
  { c: "3.2.1", n: "On Focus", s: "supports", r: "Focus alone does not initiate a change of context." },
  { c: "3.2.2", n: "On Input", s: "supports", r: "Input events do not automatically change context without user awareness." },
  { c: "3.3.1", n: "Error Identification", s: "supports", r: "Form errors are announced via toast messages and inline text with clear identification." },
  { c: "3.3.2", n: "Labels or Instructions", s: "supports", r: "Every form input has a visible label or clear instruction. Onboarding wizard includes step context." },
  { c: "4.1.1", n: "Parsing", s: "supports", r: "React ensures valid, unique-id DOM output." },
  { c: "4.1.2", n: "Name, Role, Value", s: "supports", r: "Interactive components (buttons, form fields, dialogs) expose accessible names, roles, and current values via native HTML or ARIA." },
];

const LEVEL_AA = [
  { c: "1.2.4", n: "Captions (Live)", s: "not-applicable", r: "No live audio content currently on the platform." },
  { c: "1.2.5", n: "Audio Description (Prerecorded)", s: "not-applicable", r: "No prerecorded video content currently on the platform." },
  { c: "1.3.4", n: "Orientation", s: "supports", r: "Content adapts to portrait and landscape. No forced orientation." },
  { c: "1.3.5", n: "Identify Input Purpose", s: "supports", r: "Email, phone, and name inputs use autocomplete attributes." },
  { c: "1.4.3", n: "Contrast (Minimum)", s: "partial", r: "Body text achieves 4.5:1. Brand coral (#E27D60) used only for decorative accents, large headings, backgrounds, or borders where a 3:1 ratio applies. Small body text uses coral-700 (#96442E) at 6.4:1. Full audit and remediation in progress; documented publicly at /accessibility." },
  { c: "1.4.4", n: "Resize Text", s: "supports", r: "Content scales to 200% zoom without loss of function. No fixed layouts or clipped text." },
  { c: "1.4.5", n: "Images of Text", s: "supports", r: "No images of text used for informational content." },
  { c: "1.4.10", n: "Reflow", s: "supports", r: "Content reflows at 320 CSS pixels wide without loss of information or two-dimensional scrolling." },
  { c: "1.4.11", n: "Non-text Contrast", s: "partial", r: "Interactive component borders and states meet 3:1 against background. Some coral-colored icons on cream backgrounds are being audited; documented at /accessibility." },
  { c: "1.4.12", n: "Text Spacing", s: "supports", r: "Content adapts to line height 1.5x, paragraph spacing 2x, letter spacing 0.12em, word spacing 0.16em without loss." },
  { c: "1.4.13", n: "Content on Hover or Focus", s: "supports", r: "Hover/focus popovers are dismissible, hoverable, and persistent." },
  { c: "2.4.5", n: "Multiple Ways", s: "supports", r: "Users can navigate via nav menu, footer links, and in-content links." },
  { c: "2.4.6", n: "Headings and Labels", s: "supports", r: "Headings and labels are descriptive of topic and purpose." },
  { c: "2.4.7", n: "Focus Visible", s: "supports", r: "Coral 3px focus outline visible on all interactive elements via :focus-visible." },
  { c: "2.5.5", n: "Target Size (Enhanced)", s: "supports", r: "Touch targets are at least 44x44 CSS pixels on touch devices via `pointer: coarse` media query." },
  { c: "3.1.2", n: "Language of Parts", s: "supports", r: "All content currently in English; language-tagged content will use lang attribute when i18n ships." },
  { c: "3.2.3", n: "Consistent Navigation", s: "supports", r: "Global navigation is consistent across pages within the app shell." },
  { c: "3.2.4", n: "Consistent Identification", s: "supports", r: "Components with the same function have consistent labels across the site." },
  { c: "3.3.3", n: "Error Suggestion", s: "supports", r: "Form validation provides suggested corrections when possible." },
  { c: "3.3.4", n: "Error Prevention (Legal, Financial, Data)", s: "supports", r: "Premium checkout requires explicit confirmation via Stripe hosted flow. Post-purchase, users can manage/cancel via Stripe Customer Portal." },
  { c: "4.1.3", n: "Status Messages", s: "supports", r: "Toast notifications and AI streaming use ARIA live regions (aria-live='polite', role='log') for screen-reader announcement." },
];

const STATUS_META = {
  supports: { label: "Supports", icon: CheckCircle, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-100" },
  partial: { label: "Partially supports", icon: WarningCircle, color: "text-amber-700", bg: "bg-amber-50 border-amber-100" },
  "does-not": { label: "Does not support", icon: MinusCircle, color: "text-rose-700", bg: "bg-rose-50 border-rose-100" },
  "not-applicable": { label: "Not applicable", icon: MinusCircle, color: "text-muted-foreground", bg: "bg-muted border-border" },
};

function CriteriaTable({ rows, id, title }) {
  return (
    <section id={id} className="mt-12" aria-labelledby={`${id}-h`}>
      <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-3">
        <span className="rule" />{title}
      </div>
      <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-soft">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-[11px] tracking-[0.15em] uppercase text-ink/60">
            <tr>
              <th className="p-3 w-[80px]">Criterion</th>
              <th className="p-3">Name</th>
              <th className="p-3 w-[180px]">Conformance</th>
              <th className="p-3">Remarks and explanations</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => {
              const m = STATUS_META[r.s];
              const Icon = m.icon;
              return (
                <tr key={r.c} className="align-top">
                  <td className="p-3 font-mono text-xs text-ink/70">{r.c}</td>
                  <td className="p-3 font-display text-sm">{r.n}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${m.bg}`}>
                      <Icon size={12} weight="fill" className={m.color} aria-hidden="true" />
                      <span className={m.color}>{m.label}</span>
                    </span>
                  </td>
                  <td className="p-3 text-xs text-ink/75 leading-relaxed">{r.r}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function Vpat() {
  usePageMeta({
    title: "VPAT 2.5 INT — Accessibility Conformance Report",
    description: "Wingwomen Voluntary Product Accessibility Template (VPAT 2.5 INT) covering WCAG 2.1 Level A and AA criteria.",
  });

  return (
    <div className="min-h-screen bg-cream text-ink print:bg-white">
      <header className="glass sticky top-0 z-40 print:hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="vpat-brand" className="flex items-center gap-2">
            <Flower size={22} weight="duotone" className="text-coral" aria-hidden="true" />
            <span className="font-display text-xl">Wingwomen</span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.print()}
              data-testid="vpat-print"
              className="inline-flex items-center gap-1.5 text-sm bg-ink text-cream px-4 py-2 rounded-full hover:bg-coral-700 transition-colors"
            >
              <Printer size={14} weight="duotone" aria-hidden="true" /> Print / save as PDF
            </button>
            <Link to="/accessibility" className="text-sm hover:text-coral-700 flex items-center gap-1.5" data-testid="vpat-back">
              <ArrowLeft size={14} aria-hidden="true" /> Accessibility
            </Link>
          </div>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="max-w-[1100px] mx-auto px-6 py-16 print:py-4 print:px-0">
        <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-3">
          <span className="rule" />VPAT 2.5 INT
        </div>
        <h1 className="font-display text-5xl leading-tight" data-testid="vpat-title">
          Voluntary Product<br />
          <span className="italic text-coral-700">Accessibility Report.</span>
        </h1>
        <p className="mt-5 text-ink/75 max-w-3xl leading-relaxed">
          This document is Wingwomen, Inc.'s Voluntary Product Accessibility Template
          (VPAT 2.5 INT) covering WCAG 2.1 Level A and Level AA, as required for
          Section 508 conformance and referenced by HHS Section 1557 and EN 301 549.
          It reports the conformance status of Wingwomen (the product) against
          each applicable success criterion.
        </p>

        {/* Metadata block */}
        <section className="mt-10 bg-white border border-border rounded-2xl overflow-hidden shadow-card">
          <dl className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border">
            <div className="p-5">
              <dt className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Vendor company</dt>
              <dd className="font-display text-lg mt-1">Wingwomen, Inc.</dd>
            </div>
            <div className="p-5">
              <dt className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Product name and version</dt>
              <dd className="font-display text-lg mt-1">Wingwomen (web), v1.0</dd>
            </div>
            <div className="p-5 sm:border-t sm:border-border">
              <dt className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Report date</dt>
              <dd className="font-display text-lg mt-1">February 2026</dd>
            </div>
            <div className="p-5 sm:border-t sm:border-border">
              <dt className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Contact for accessibility</dt>
              <dd className="font-display text-lg mt-1">
                <a href="mailto:hi@mywingwomen.com" className="text-coral-700 underline underline-offset-4">
                  hi@mywingwomen.com
                </a>
              </dd>
            </div>
            <div className="p-5 border-t border-border sm:col-span-2">
              <dt className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Standards applied</dt>
              <dd className="mt-2 flex flex-wrap gap-2 text-xs">
                <span className="px-2.5 py-1 bg-coral-50 border border-coral-100 rounded-full text-coral-700">WCAG 2.1 Level A</span>
                <span className="px-2.5 py-1 bg-coral-50 border border-coral-100 rounded-full text-coral-700">WCAG 2.1 Level AA</span>
                <span className="px-2.5 py-1 bg-sage-50 border border-sage-100 rounded-full text-sage-700">Section 508 (Revised, 2017)</span>
                <span className="px-2.5 py-1 bg-sage-50 border border-sage-100 rounded-full text-sage-700">EN 301 549 (European)</span>
              </dd>
            </div>
          </dl>
        </section>

        {/* Evaluation methods */}
        <section className="mt-10 bg-white border border-border rounded-2xl p-6 shadow-soft text-sm text-ink/80 leading-relaxed">
          <h2 className="font-display text-2xl">Evaluation methods</h2>
          <p className="mt-3">
            This VPAT was completed via internal engineering review of every criterion in
            WCAG 2.1 Level A and Level AA against the deployed product at
            https://mywingwomen.com. Manual testing included keyboard-only navigation
            across all core user flows, screen-reader validation using macOS VoiceOver
            and NVDA on Windows, contrast measurement using WebAIM's Contrast Checker,
            and automated auditing with axe DevTools and Chrome Lighthouse. A third-party
            audit is scheduled for Q2 2026 and will supersede this internal report.
          </p>
        </section>

        {/* Conformance summary */}
        <section className="mt-10">
          <h2 className="font-display text-2xl">Conformance summary</h2>
          <div className="mt-4 grid sm:grid-cols-3 gap-4">
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5">
              <div className="text-[10px] tracking-[0.25em] uppercase text-emerald-700 flex items-center gap-1.5">
                <CheckCircle size={12} weight="fill" aria-hidden="true" /> Supports
              </div>
              <div className="font-display text-4xl text-emerald-700 mt-2">
                {LEVEL_A.filter(r => r.s === "supports").length + LEVEL_AA.filter(r => r.s === "supports").length}
              </div>
              <div className="text-xs text-emerald-900/70 mt-1">of {LEVEL_A.length + LEVEL_AA.length} A + AA criteria</div>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
              <div className="text-[10px] tracking-[0.25em] uppercase text-amber-700 flex items-center gap-1.5">
                <WarningCircle size={12} weight="fill" aria-hidden="true" /> Partially supports
              </div>
              <div className="font-display text-4xl text-amber-700 mt-2">
                {LEVEL_A.filter(r => r.s === "partial").length + LEVEL_AA.filter(r => r.s === "partial").length}
              </div>
              <div className="text-xs text-amber-900/70 mt-1">with remediation timeline in remarks</div>
            </div>
            <div className="bg-muted border border-border rounded-2xl p-5">
              <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground flex items-center gap-1.5">
                <MinusCircle size={12} weight="fill" aria-hidden="true" /> Not applicable
              </div>
              <div className="font-display text-4xl text-ink mt-2">
                {LEVEL_A.filter(r => r.s === "not-applicable").length + LEVEL_AA.filter(r => r.s === "not-applicable").length}
              </div>
              <div className="text-xs text-muted-foreground mt-1">criterion doesn't apply to this product</div>
            </div>
          </div>
        </section>

        <CriteriaTable rows={LEVEL_A} id="level-a" title="WCAG 2.1 Level A criteria" />
        <CriteriaTable rows={LEVEL_AA} id="level-aa" title="WCAG 2.1 Level AA criteria" />

        {/* Section 508 mapping */}
        <section className="mt-12 bg-white border border-border rounded-2xl p-6 shadow-soft text-sm text-ink/80 leading-relaxed">
          <h2 className="font-display text-2xl">Section 508 Chapter 5, Software</h2>
          <p className="mt-3">
            Section 508 Chapter 5 (Software) references WCAG 2.0 Level A and AA criteria
            directly. As of the 2024 update, WCAG 2.1 Level AA is the effective standard
            for federal procurement. All WCAG 2.1 A and AA criteria above satisfy the
            functional performance criteria in Section 508 §501 through §504.
          </p>
        </section>

        {/* Legal footer */}
        <section className="mt-12 text-xs text-muted-foreground leading-relaxed border-t border-border pt-6">
          <p>
            <strong>Disclaimer.</strong> This VPAT is provided as an evaluation tool and
            for informational purposes only. Wingwomen, Inc. makes reasonable efforts to
            ensure the accuracy of this report. Product features and their accessibility
            are subject to ongoing improvement. This report does not constitute a legal
            representation or warranty of accessibility conformance. Wingwomen, Inc.
            reserves the right to update this VPAT as the product evolves.
          </p>
          <p className="mt-3">© 2021–2026 Wingwomen, Inc. All rights reserved.</p>
        </section>
      </main>

      <SiteFooter compact />
    </div>
  );
}
